import { __decorate } from "tslib";
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import bb, { zoom, areaLineRange } from 'billboard.js';
import DaysBeforeOptions from '@/entities/enums/DaysBeforeOptions';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import AxiosService from '@/services/api/AxiosService';
import ApiLinks from '@/entities/ApiLinks';
import AppConfig from '@/configLoader';
let LoggerRssiChart = class LoggerRssiChart extends Vue {
    constructor() {
        super(...arguments);
        this.DaysBeforeOptions = DaysBeforeOptions;
        this.selectedDayOption = DaysBeforeOptions.ONE;
        this.isLoading = false;
        this.chart = null;
    }
    get hasData() {
        return this.rssi && this.rssi.length > 0;
    }
    get rssiWarningThreshold() {
        return AppConfig.getConfig().attributes?.rssi_thresholds?.warning || -60;
    }
    get rssiAlarmThreshold() {
        return AppConfig.getConfig().attributes?.rssi_thresholds?.alarm || -80;
    }
    rssiUpdated() {
        if (this.rssi?.length > 0) {
            this.$nextTick(() => {
                const data = this.processData(JSON.parse(JSON.stringify(this.rssi)));
                this.buildChart(data);
            });
        }
    }
    created() {
        if (this.rssi?.length > 0) {
            this.$nextTick(() => {
                const data = this.processData(JSON.parse(JSON.stringify(this.rssi)));
                this.buildChart(data);
                if (this.source.hasLogger()) {
                    this.openSocket();
                }
            });
        }
    }
    /*Close websocket if exists */
    async beforeDestroy() {
        this.destroyChart();
        await this.closeSocket();
    }
    processData(rssiData) {
        const signalMax = Math.max(...this.rssi.map((x) => x[3]).filter((x) => x != null));
        const signalMin = Math.min(...this.rssi.map((x) => x[1]).filter((x) => x != null));
        let times = [];
        rssiData.forEach((rssi) => {
            let val = rssi.shift();
            times.push(new Date(val * 1000));
        });
        let values = ['signalData'];
        let xData = ['x'].concat(times);
        let yData = values.concat(rssiData);
        return { x: xData, y: yData, max: signalMax, min: signalMin };
    }
    buildChart(data) {
        let self = this;
        this.chart = bb.generate({
            data: {
                x: 'x',
                xSort: true,
                columns: [data.x, data.y],
                types: {
                    signalData: areaLineRange()
                },
                names: {
                    signalData: this.$t('loggers.signal.title')
                },
                colors: {
                    signalData: '#333333'
                }
            },
            axis: {
                x: {
                    tick: {
                        fit: false,
                        count: 5,
                        format: (x) => {
                            return this.dateTimeManager.formatTime(new Date(x), null, null, null);
                        }
                    },
                    type: 'timeseries'
                },
                y: {
                    label: this.$t('loggers.signal.title'),
                    max: data.max,
                    min: data.min,
                    tick: {
                        format: function (x) {
                            return x + ' dBm';
                        }
                    }
                }
            },
            zoom: {
                enabled: zoom()
            },
            point: {
                focus: {
                    only: true
                }
            },
            tooltip: {
                contents: function (d) {
                    let index = d[0].index; // Get the index of the data point
                    let val = self.rssi[index]; // Get min, avg, and max values
                    return `
              <div class="custom-tooltip">
                <div class="tooltip-content">
                  <span> ${self.dateTimeManager.formatTime(new Date(val[0] * 1000), null, null, null)}</span>
                  <div><span class="tooltip-label">${self.$t('loggers.signal.max')}:</span> <span class="tooltip-value">${val[3]} dBm</span></div>
                  <div><span class="tooltip-label has-text-weight-bold">${self.$t('loggers.signal.avg')}:</span> <span class="tooltip-value has-text-weight-bold">${val[2]} dBm</span></div>
                   <div><span class="tooltip-label">${self.$t('loggers.signal.min')}:</span> <span class="tooltip-value">${val[1]} dBm</span></div>
                </div>
              </div>
            `;
                }
            },
            regions: [
                {
                    axis: 'y',
                    start: this.rssiWarningThreshold,
                    class: 'chart_fill_green'
                },
                {
                    axis: 'y',
                    start: this.rssiAlarmThreshold,
                    end: this.rssiWarningThreshold,
                    class: 'chart_fill_yellow'
                },
                {
                    axis: 'y',
                    end: this.rssiAlarmThreshold,
                    class: 'chart_fill_red'
                }
            ],
            bindto: '#signalStrengthChart'
        });
    }
    dayOptionChanged() {
        let loadFrom = new Date();
        loadFrom.setDate(loadFrom.getDate() - this.selectedDayOption);
        this.destroyChart();
        this.$emit('loadSignal', loadFrom);
    }
    destroyChart() {
        if (this.chart) {
            this.chart.destroy();
        }
    }
    /*Open websocket for signal strength */
    openSocket() {
        if (!this.connection)
            this.listenSignalStrength();
    }
    /*Close websocket for signal strength */
    closeSocket() {
        if (this.connection)
            return this.connection.stop();
    }
    listenSignalStrength() {
        //Security
        let options = {
            accessTokenFactory: () => {
                return new Promise((resolve, reject) => {
                    let token = AxiosService.GetWebSocketOptions(this);
                    if (token == null || token.length === 0)
                        reject();
                    resolve(token);
                });
            }
        };
        //open connection
        this.connection = new HubConnectionBuilder()
            .withUrl(`${AppConfig.getConfig().backend.address}${ApiLinks.SignalStrength.Logger}`, options)
            .configureLogging(LogLevel.Information)
            .build();
        this.connection
            .start()
            .then(() => {
            this.connection.invoke('joinGroup', this.source.logger.companyLoggerId);
        })
            .catch((error) => console.log(error));
        this.connection.on('LoggerSignalStrengthUpdated', (data) => {
            this.addPointToChart(data.MeasuredAt, data.Rssi);
        });
    }
    addPointToChart(ts, value) {
        let time = new Date(ts * 1000);
        this.rssi.push([ts, value, value, value]);
        this.chart.flow({
            columns: [
                ['x', time], // X-axis (timestamp)
                ['signalData', [value - 2, value, value + 2]] // Y-axis (value)
            ],
            duration: 500 // Optional animation duration
        });
    }
};
__decorate([
    Prop({ type: Object })
], LoggerRssiChart.prototype, "dateTimeManager", void 0);
__decorate([
    Prop({ type: Array })
], LoggerRssiChart.prototype, "rssi", void 0);
__decorate([
    Prop({ type: Object })
], LoggerRssiChart.prototype, "source", void 0);
__decorate([
    Prop({ type: Boolean })
], LoggerRssiChart.prototype, "isLoadingSignal", void 0);
__decorate([
    Watch('rssi')
], LoggerRssiChart.prototype, "rssiUpdated", null);
LoggerRssiChart = __decorate([
    Component({ components: {} })
], LoggerRssiChart);
export default LoggerRssiChart;
